import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import { getContactId, getCustomerDisplayName, extractCustomerDetails, extractClientDetails } from '../utils/contactHelpers';
import DocumentModalShell from './DocumentModalShell';
import {
  numberToWords,
  parseCompanyProfile,
  resolveBillToForReceipt,
  getReceiptDisplayLineItems,
  mapApiLineItemToRow,
  getLineItemLineTotal,
} from '../utils/documentModalHelpers';
import { FileText, Search, AlertCircle, CheckCircle, Eye, Edit, Trash2, CheckCircle2, X } from 'lucide-react';

/** Read-only services/items grid — matches TaxInvoiceForm column layout. */
const ReceiptServicesTable = ({ items }) => {
  if (!items?.length) {
    return (
      <p className="text-sm text-gray-500">
        No services or items to show. Link an invoice when creating the receipt to display invoice line items here;
        otherwise the payment amount is summarized below.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
      <div className="inline-block min-w-full align-middle">
        <table className="min-w-[640px] sm:min-w-full w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">S.No</th>
              <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">SAC / HSN Code</th>
              <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium min-w-[150px]">Description of Goods / Services</th>
              <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">Quantity</th>
              <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">Amount (₹)</th>
              <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">Line Total (₹)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const row = mapApiLineItemToRow(item, index);
              const codeDisp = (row.item_type === 'service' ? row.sac_code : row.hsn_code) || '—';
              const qtyDisp = row.item_type === 'service' ? '—' : String(row.quantity ?? 1);
              const lineTotal = getLineItemLineTotal(item);
              return (
                <tr key={row.id}>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">{index + 1}</td>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">{codeDisp}</td>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">{row.description || '—'}</td>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">{qtyDisp}</td>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">
                    {Number(row.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium">
                    {lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Receipts = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentType, setFilterPaymentType] = useState('all');
  const [ordering, setOrdering] = useState('-payment_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editReceipt, setEditReceipt] = useState(null);
  const [editForm, setEditForm] = useState({
    status: 'draft',
    customer: '',
    invoice: '',
    receipt_number: '',
    payment_date: '',
    payment_type: '',
    amount: '',
    reference_number: '',
    notes: '',
    customer_name: '',
    customer_company: '',
    customer_address: '',
    customer_gstin: '',
    customer_pan: ''
  });
  const [editContactType, setEditContactType] = useState('');
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editCustomers, setEditCustomers] = useState([]);
  const [editClients, setEditClients] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [viewBillTo, setViewBillTo] = useState(null);
  const [viewLineItems, setViewLineItems] = useState([]);
  const [editLineItems, setEditLineItems] = useState([]);

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const fetchReceipts = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPaymentType !== 'all') params.append('payment_type', filterPaymentType);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-payment_date');

      const response = await axios.get(
        `${API_ENDPOINTS.RECEIPTS_LIST}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const data = response.data;
      const normalized = Array.isArray(data) ? data : (data.results || []);
      const normalizedCount = Array.isArray(data) ? data.length : (data.count || normalized.length || 0);

      setItems(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch receipts'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReceipts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterPaymentType, ordering]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchReceipts(1);
    }, 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (!isEditOpen || !editReceipt?.id) {
      setEditLineItems([]);
      return;
    }
    const inv = editForm.invoice;
    const load = async () => {
      if (!inv) {
        setEditLineItems(getReceiptDisplayLineItems(editReceipt, null));
        return;
      }
      const id = parseInt(String(inv), 10);
      if (!Number.isFinite(id)) {
        setEditLineItems(getReceiptDisplayLineItems(editReceipt, null));
        return;
      }
      try {
        const r = await axios.get(API_ENDPOINTS.INVOICES_DETAIL(id), { headers: getAuthHeaders() });
        setEditLineItems(getReceiptDisplayLineItems(editReceipt, r.data));
      } catch {
        setEditLineItems(getReceiptDisplayLineItems(editReceipt, null));
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditOpen, editForm.invoice, editReceipt?.id, editReceipt?.amount, editReceipt?.notes]);

  useEffect(() => {
    if (isDetailsOpen || isEditOpen) {
      axios.get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers: getAuthHeaders() })
        .then((res) => setCompanyProfile(parseCompanyProfile(res.data)))
        .catch(() => setCompanyProfile(null));
    }
  }, [isDetailsOpen, isEditOpen]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.CUSTOMERS_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || []);
      setEditCustomers(normalized);
      return normalized;
    } catch (err) {
      return [];
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || data.clients || []);
      setEditClients(normalized);
      return normalized;
    } catch (err) {
      return [];
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleView = async (receipt) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        API_ENDPOINTS.RECEIPTS_DETAIL(receipt.id),
        { headers: getAuthHeaders() }
      );
      const data = response.data;
      const invoiceId = data.invoice
        ? typeof data.invoice === 'object'
          ? data.invoice.id
          : data.invoice
        : null;
      let invoiceDetail = null;
      if (invoiceId) {
        try {
          const invRes = await axios.get(API_ENDPOINTS.INVOICES_DETAIL(invoiceId), {
            headers: getAuthHeaders(),
          });
          invoiceDetail = invRes.data;
        } catch {
          /* ignore */
        }
      }
      const [custList, clientList] = await Promise.all([fetchCustomers(), fetchClients()]);
      setViewBillTo(resolveBillToForReceipt(data, custList, clientList, invoiceDetail));
      setViewLineItems(getReceiptDisplayLineItems(data, invoiceDetail));
      setSelectedReceipt(data);
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch receipt details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (receipt) => {
    try {
      setIsEditLoading(true);
      const detailResponse = await axios.get(API_ENDPOINTS.RECEIPTS_DETAIL(receipt.id), {
        headers: getAuthHeaders(),
      });
      const data = detailResponse.data || receipt;
      const invoiceId = data.invoice
        ? typeof data.invoice === 'object'
          ? data.invoice.id
          : data.invoice
        : null;
      let invoiceDetail = null;
      if (invoiceId) {
        try {
          const invRes = await axios.get(API_ENDPOINTS.INVOICES_DETAIL(invoiceId), {
            headers: getAuthHeaders(),
          });
          invoiceDetail = invRes.data;
        } catch {
          /* ignore */
        }
      }
      const [custList, clientList] = await Promise.all([fetchCustomers(), fetchClients()]);
      setEditReceipt(data);
      const contactTypeInit =
        data.customer != null && data.customer !== ''
          ? 'customer'
          : data.client != null && data.client !== ''
            ? 'client'
            : '';
      setEditContactType(contactTypeInit);
      const customerId = getContactId(data.customer) || getContactId(data.client);
      const invoiceIdForForm = data.invoice
        ? typeof data.invoice === 'object'
          ? data.invoice?.id
          : data.invoice
        : '';
      const customerDetails = resolveBillToForReceipt(data, custList, clientList, invoiceDetail);
      setEditForm({
        status: data.status || 'draft',
        customer: customerId,
        invoice: invoiceIdForForm,
        receipt_number: data.receipt_number || '',
        payment_date: data.payment_date ? data.payment_date.split('T')[0] : '',
        payment_type: data.payment_type || 'cash',
        amount: data.amount || '',
        reference_number: data.reference_number || '',
        notes: data.notes || '',
        customer_name: customerDetails.name,
        customer_company: customerDetails.company,
        customer_address: customerDetails.address,
        customer_gstin: customerDetails.gstin,
        customer_pan: customerDetails.pan,
      });
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load receipt for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editReceipt?.id) return;
    if (!editContactType || !editForm.customer) {
      setError('Please select a customer or client before updating.');
      return;
    }
    if (!editForm.payment_date) {
      setError('Payment date is required.');
      return;
    }
    setIsEditLoading(true);
    try {
      const payload = {
        status: editForm.status,
        receipt_number: editForm.receipt_number || null,
        payment_date: editForm.payment_date,
        payment_type: editForm.payment_type || 'cash',
        amount: editForm.amount?.toString() || '0',
        reference_number: editForm.reference_number || '',
        notes: editForm.notes || '',
        invoice: editForm.invoice ? parseInt(editForm.invoice) : null,
        customer: editContactType === 'customer' && editForm.customer ? parseInt(editForm.customer) : null,
        client: editContactType === 'client' && editForm.customer ? parseInt(editForm.customer) : null
      };
      const response = await axios.put(
        API_ENDPOINTS.RECEIPTS_UPDATE(editReceipt.id),
        payload,
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Receipt updated successfully';
      setError('');
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setEditReceipt(null);
      setTimeout(() => {
        fetchReceipts(currentPage);
        setSuccessMessage('');
      }, 1200);
    } catch (err) {
      console.error('Receipt update error', err?.response?.data || err);
      setError(getErrorMessage(err, 'Failed to update receipt'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleOpenDelete = (receipt) => {
    setDeleteTarget(receipt);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(
        API_ENDPOINTS.RECEIPTS_DELETE(deleteTarget.id),
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Receipt deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchReceipts(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete receipt'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleMarkCompleted = async (receipt) => {
    if (!receipt?.id) return;
    setIsMarking(true);
    try {
      const response = await axios.post(
        API_ENDPOINTS.RECEIPTS_MARK_COMPLETED(receipt.id),
        {},
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.status || 'Receipt marked as completed';
      setSuccessMessage(msg);
      fetchReceipts(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to mark receipt as completed'));
    } finally {
      setIsMarking(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Receipts</p>
              <p className="text-2xl font-bold text-foreground">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search receipts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={filterPaymentType}
            onChange={(e) => setFilterPaymentType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Payment Types</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cheque">Cheque</option>
            <option value="upi">UPI</option>
            <option value="other">Other</option>
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-payment_date">Newest</option>
            <option value="payment_date">Oldest</option>
            <option value="-amount">Amount: High to Low</option>
            <option value="amount">Amount: Low to High</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer / Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((receipt) => (
                <tr key={receipt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {receipt.receipt_number || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {receipt.customer_name || receipt.client_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ₹{parseFloat(receipt.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {receipt.payment_date ? new Date(receipt.payment_date).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                    {receipt.payment_type || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(receipt.status)}`}>
                      {receipt.status || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleView(receipt)}
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(receipt)}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delete"
                        onClick={() => handleOpenDelete(receipt)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Mark Completed"
                        onClick={() => handleMarkCompleted(receipt)}
                        disabled={receipt.status === 'completed' || isMarking}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {(nextPage || prevPage) && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing page {currentPage} of {Math.ceil(totalCount / 10)}
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchReceipts(currentPage - 1)}
                  disabled={!prevPage}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchReceipts(currentPage + 1)}
                  disabled={!nextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal — layout aligned with TaxInvoiceForm (document left, Bill To right, services, totals, bank) */}
      {isDetailsOpen && selectedReceipt && (
        <DocumentModalShell
          title="Receipt Details"
          onClose={() => {
            setIsDetailsOpen(false);
            setSelectedReceipt(null);
            setViewBillTo(null);
            setViewLineItems([]);
          }}
          banner="RECEIPT"
          companyProfile={companyProfile}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div><p className="text-sm font-medium text-gray-700 mb-1">Receipt No</p><p className="font-semibold">{selectedReceipt.receipt_number || 'N/A'}</p></div>
              <div><p className="text-sm font-medium text-gray-700 mb-1">Receipt Date</p><p className="font-semibold">{selectedReceipt.payment_date ? new Date(selectedReceipt.payment_date).toLocaleDateString() : 'N/A'}</p></div>
              <div><p className="text-sm font-medium text-gray-700 mb-1">Payment Type</p><p className="font-semibold capitalize">{selectedReceipt.payment_type || 'N/A'}</p></div>
              <div><p className="text-sm font-medium text-gray-700 mb-1">Status</p><p className="font-semibold capitalize">{selectedReceipt.status || 'N/A'}</p></div>
              <div><p className="text-sm font-medium text-gray-700 mb-1">Reference #</p><p className="font-semibold">{selectedReceipt.reference_number || 'N/A'}</p></div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Invoice</p>
                <p className="font-semibold">
                  {selectedReceipt.invoice
                    ? typeof selectedReceipt.invoice === 'object'
                      ? [selectedReceipt.invoice?.invoice_number, selectedReceipt.invoice?.id != null ? `#${selectedReceipt.invoice.id}` : null].filter(Boolean).join(' · ') || 'N/A'
                      : selectedReceipt.invoice
                    : 'N/A'}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Bill To</h3>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Name</p>
                <p className="font-semibold">
                  {viewBillTo?.name ||
                    selectedReceipt.customer_name ||
                    selectedReceipt.client_name ||
                    (typeof selectedReceipt.customer === 'object' ? (selectedReceipt.customer?.customer_name || selectedReceipt.customer?.name) : null) ||
                    (typeof selectedReceipt.client === 'object' ? (selectedReceipt.client?.name || selectedReceipt.client?.company_name) : null) ||
                    'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Company</p>
                <p className="font-semibold">
                  {viewBillTo?.company ||
                    selectedReceipt.customer_company ||
                    selectedReceipt.client_company ||
                    (typeof selectedReceipt.customer === 'object' ? selectedReceipt.customer?.company_name : null) ||
                    (typeof selectedReceipt.client === 'object' ? selectedReceipt.client?.company_name : null) ||
                    'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Address</p>
                <p className="font-semibold">
                  {viewBillTo?.address ||
                    selectedReceipt.customer_address ||
                    selectedReceipt.client_address ||
                    (typeof selectedReceipt.customer === 'object' ? selectedReceipt.customer?.address : null) ||
                    (typeof selectedReceipt.client === 'object' ? selectedReceipt.client?.address : null) ||
                    'N/A'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">GSTIN</p>
                  <p className="font-semibold">
                    {viewBillTo?.gstin ||
                      selectedReceipt.customer_gstin ||
                      selectedReceipt.client_gstin ||
                      (typeof selectedReceipt.customer === 'object' ? (selectedReceipt.customer?.gstin || selectedReceipt.customer?.gst_number) : null) ||
                      (typeof selectedReceipt.client === 'object' ? (selectedReceipt.client?.gstin || selectedReceipt.client?.gst_number) : null) ||
                      'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">PAN</p>
                  <p className="font-semibold">
                    {viewBillTo?.pan ||
                      selectedReceipt.customer_pan ||
                      selectedReceipt.client_pan ||
                      (typeof selectedReceipt.customer === 'object' ? selectedReceipt.customer?.pan : null) ||
                      (typeof selectedReceipt.client === 'object' ? selectedReceipt.client?.pan : null) ||
                      'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Services</h3>
            <ReceiptServicesTable items={viewLineItems} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="space-y-2">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-700">Total Taxable Value:</span>
                  <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">
                    ₹
                    {viewLineItems.reduce((s, it) => s + getLineItemLineTotal(it), 0).toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="font-semibold">Total Received:</span>
                  <span className="font-semibold">
                    ₹
                    {parseFloat(selectedReceipt.amount || 0).toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-600 pt-2">
                  Total Received (in words): {numberToWords(Math.floor(parseFloat(selectedReceipt.amount || 0)))} Rupees Only
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Bank Details for Payment</h3>
              <div className="space-y-2 text-sm">
                <div><span className="text-gray-600">Account Name:</span> <span>{companyProfile?.accountName || '—'}</span></div>
                <div><span className="text-gray-600">Bank Name:</span> <span>{companyProfile?.bankName || '—'}</span></div>
                <div><span className="text-gray-600">Account No:</span> <span>{companyProfile?.accountNo || '—'}</span></div>
                <div><span className="text-gray-600">IFSC:</span> <span>{companyProfile?.ifscCode || '—'}</span></div>
              </div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Signature</h3>
              <p className="text-sm text-gray-600">For {companyProfile?.name || 'COMPANY NAME'}</p>
              <div className="mt-8 border-t border-gray-300 pt-2"><p className="text-sm">Authorized Signatory</p></div>
            </div>
          </div>
          {selectedReceipt.notes && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <p className="text-sm text-gray-600 border border-gray-200 rounded-md p-3 bg-gray-50">{selectedReceipt.notes}</p>
            </div>
          )}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => {
                setIsDetailsOpen(false);
                setSelectedReceipt(null);
                setViewBillTo(null);
                setViewLineItems([]);
              }}
            >
              Close
            </Button>
          </div>
        </DocumentModalShell>
      )}

      {/* Edit Modal — section order matches TaxInvoiceForm receipt creation */}
      {isEditOpen && editReceipt && (
        <DocumentModalShell
          title="Update Receipt"
          onClose={() => {
            setIsEditOpen(false);
            setEditReceipt(null);
            setEditLineItems([]);
          }}
          banner="RECEIPT"
          companyProfile={companyProfile}
        >
          <form onSubmit={handleSubmitEdit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receipt No</label>
                  <input
                    type="text"
                    value={editForm.receipt_number}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, receipt_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Date</label>
                  <input
                    type="date"
                    value={editForm.payment_date}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, payment_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                  <select
                    value={editForm.payment_type}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, payment_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <input
                    type="text"
                    value={editForm.reference_number}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, reference_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="UTR / cheque / ref"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice (ID)</label>
                  <input
                    type="text"
                    value={editForm.invoice}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, invoice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Linked sales invoice id"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Received</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                  <select
                    value={editContactType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditContactType(v);
                      setEditForm((prev) => ({
                        ...prev,
                        customer: '',
                        customer_name: '',
                        customer_company: '',
                        customer_address: '',
                        customer_gstin: '',
                        customer_pan: '',
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select type</option>
                    <option value="customer">Customer</option>
                    <option value="client">Client</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editContactType === 'client' ? 'Select Client' : 'Select Customer'}
                  </label>
                  <select
                    value={editForm.customer}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && editContactType === 'customer') {
                        const c = editCustomers.find((x) => String(x.id) === val);
                        if (c) {
                          const d = extractCustomerDetails(c);
                          setEditForm((prev) => ({
                            ...prev,
                            customer: val,
                            customer_name: d.name,
                            customer_company: d.company,
                            customer_address: d.address,
                            customer_gstin: d.gstin,
                            customer_pan: d.pan,
                          }));
                        } else setEditForm((prev) => ({ ...prev, customer: val }));
                      } else if (val && editContactType === 'client') {
                        const c = editClients.find((x) => String(x.id) === val);
                        if (c) {
                          const d = extractClientDetails(c);
                          setEditForm((prev) => ({
                            ...prev,
                            customer: val,
                            customer_name: d.name,
                            customer_company: d.company,
                            customer_address: d.address,
                            customer_gstin: d.gstin,
                            customer_pan: d.pan,
                          }));
                        } else setEditForm((prev) => ({ ...prev, customer: val }));
                      } else {
                        setEditForm((prev) => ({
                          ...prev,
                          customer: val,
                          customer_name: '',
                          customer_company: '',
                          customer_address: '',
                          customer_gstin: '',
                          customer_pan: '',
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={!editContactType}
                  >
                    <option value="">Select</option>
                    {editContactType === 'client'
                      ? editClients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.client_name || c.company_name || `Client ${c.id}`}
                          </option>
                        ))
                      : editCustomers.map((c) => (
                          <option key={c.id} value={c.id}>
                            {getCustomerDisplayName(c)}
                          </option>
                        ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editForm.customer_name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={editForm.customer_company}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, customer_company: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={editForm.customer_address}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, customer_address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                    <input
                      type="text"
                      value={editForm.customer_gstin}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, customer_gstin: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                    <input
                      type="text"
                      value={editForm.customer_pan}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, customer_pan: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Services</h3>
              <p className="text-xs text-gray-500 mb-2">
                Line items come from the linked invoice when an invoice ID is set. They are read-only here; amounts are saved as Total Received.
              </p>
              <ReceiptServicesTable items={editLineItems} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-xs sm:text-sm text-gray-700">Total Taxable Value:</span>
                    <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">
                      ₹
                      {editLineItems
                        .reduce((s, it) => s + getLineItemLineTotal(it), 0)
                        .toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-2 pt-2 border-t border-gray-300">
                    <span className="text-sm sm:text-lg font-semibold">Total Received:</span>
                    <span className="text-sm sm:text-lg font-semibold whitespace-nowrap">
                      ₹
                      {parseFloat(editForm.amount || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 pt-2 break-words">
                    Total Received (in words): {numberToWords(Math.floor(parseFloat(editForm.amount || 0)))} Rupees Only
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Bank Details for Payment</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="text-gray-600 min-w-[100px] sm:min-w-[120px]">Account Name:</span>
                    <span className="flex-1 break-words">{companyProfile?.accountName || '—'}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="text-gray-600 min-w-[100px] sm:min-w-[120px]">Bank Name:</span>
                    <span className="flex-1 break-words">{companyProfile?.bankName || '—'}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="text-gray-600 min-w-[100px] sm:min-w-[120px]">Account No:</span>
                    <span className="flex-1 break-words">{companyProfile?.accountNo || '—'}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="text-gray-600 min-w-[100px] sm:min-w-[120px]">IFSC:</span>
                    <span className="flex-1 break-words">{companyProfile?.ifscCode || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={3}
                value={editForm.notes}
                onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditOpen(false);
                  setEditReceipt(null);
                  setEditLineItems([]);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isEditLoading}>
                {isEditLoading ? 'Updating...' : 'Update Receipt'}
              </Button>
            </div>
          </form>
        </DocumentModalShell>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Receipt</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete{' '}
                <span className="font-semibold">{deleteTarget.receipt_number || `#${deleteTarget.id}`}</span>?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setIsDeleteOpen(false); setDeleteTarget(null); }} disabled={isDeleteLoading}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-[100]">
          <div className="bg-green-600 text-white px-4 py-3 rounded-md shadow-lg flex items-center space-x-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">{successMessage}</span>
            <button onClick={() => setSuccessMessage('')} className="text-white/80 hover:text-white text-xs">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receipts;

